import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Row, Col, Button, Form } from "react-bootstrap";
import { ProfessionalDto, UseGetProfessionalsObjectResult } from "../types";
import { useState } from "react";
import AlertMessage from "../../../components/AlertMessage";
import { setCurrentPage, setSearchText } from "../../../redux/historicalSlice";
import { RootState } from "../../../redux/store";
import { useAlert } from "../../../hooks/useAlert";
import AppTable, { ColumnConfig } from "../../../components/AppTable/AppTable";
import { SectionPermissionTypeEnum } from "../../../types/SectionPermissionTypesEnum";
import ModalGlobal from "../../../components/AppModal/ModalGlobal";
import { useModal } from "../../../hooks/useModal";
import { getProfessionalModalMotiveDeleteSchema } from "../schema/ProfessionalSchema";
import { activeProfessional, deleteProfessional, exportCsvProfessional } from "../api/professionalAPI";
import { useGetProfessionals } from "../hooks/useGetProfessionals";
import Search from "../../../components/Search";
import { setFilterByOnlyActiveRecords, setHideFilter } from "../../../redux/filtersSlice";
import { useFiltersProfessionals } from "../hooks/useFiltersProfessionals";
import FiltersContainer from "../../../components/Filters/components/FiltersContainer";

export default function ProfessionalList() {

    //basic imports
    const { t } = useTranslation(['common', 'professional']);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { data: filters } = useFiltersProfessionals();

    const currentPage = useSelector((state: RootState) => state.historical.professionals.currentPage);
    const itemsPerPage = useSelector((state: RootState) => state.historical.professionals.itemsPerPage);
    const searchText = useSelector((state: RootState) => state.historical.professionals.searchText);
    const onlyActiveToggleChange = useSelector((state: RootState) => state.filters.professionals.filterByOnlyActiveRecords);
    const selectedFilters = useSelector((state: RootState) => state.filters.professionals.selectedFilters);


    const [isFilterVisible, setIsFilterVisible] = useState<boolean>(false)
    const isHideFilter = useSelector((state: RootState) => state.filters.professionals.hideFilter);

    const { hideAlert, alert, isVisible } = useAlert({ fromLocation: true });

    const [searchTerm, setSearchTerm] = useState("");

    const { data: professionalsPagination, isLoading, error, refetch } = useGetProfessionals({
        search: searchTerm
    });

    const handleClear = () => {
        dispatch(setCurrentPage({ table: "professionals", page: 1 }));
        dispatch(setSearchText({ table: "professionals", text: '' }));
        setSearchTerm("");
    }

    const handleSearchInput = () => {
        setSearchTerm(searchText || '');
        dispatch(setCurrentPage({ table: "professionals", page: 1 }));
    };

    const handleFilterByActives = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setFilterByOnlyActiveRecords({ table: 'professionals', filterByOnlyActiveRecords: e.target.checked }));
    };

    const handleClick = () => {
        dispatch(setHideFilter({ table: 'professionals', hide: false }));  // Ejecutar el dispatch de la acci?n
        setIsFilterVisible(!isFilterVisible); // Cambiar el estado local
    };

    const handleExportCsv = async () => {
        //setLoading(true);

        try {
            await exportCsvProfessional({
                pageNumber: currentPage,
                pageSize: itemsPerPage,
                search: searchText,
                tableOrigin: "professionals",
                filters: selectedFilters,
                selectedOrganizationalStructureIds: [],
                filterByOnlyActiveRecords: onlyActiveToggleChange
            });

            /*
            showAlert({
                message: t("participant:modal.exportSuccessCSV"),
                type: "success"
            });*/
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message
           //  setError(t("common:requestError.ErrorBadRequest") ?? errorMessage);
        } finally {
            //setLoading(false);
        }
    };


    const columns: ColumnConfig<ProfessionalDto>[] = [
        { header: t("professional:headerTableId"), key: "id", render: (item) => <b>{item.id}</b> },
        { header: t("professional:headerTableNameAndSurname"), key: "nameAndSurname" },
        { header: t("professional:headerTableEmail"), key: "email" },
        { header: t("professional:headerTableService"), key: "serviceIndicator" },
        { header: t("professional:headerProfessionalType"), key: "professionalType" },
        { header: t("common:form.fields.status"), key: "status", render: (item) => <div style={{ color: item.isActive ?  "inherit": "gray" }}>{item.isActive ? t("common:stateActive") : t("common:stateInactive")}</div> },
       
    ];
  
    const { showModal, modalData, motiveDelete, openModal, closeModal, errorModal, setMotiveDelete, setErrorModal, currentId } = useModal<ProfessionalDto>();

    const confirmSubmitModal = async () => {
        const schema = getProfessionalModalMotiveDeleteSchema(t);
        if (modalData.type === 'delete') {
            try {
                await schema.validate({ motiveDelete }, { abortEarly: false });

            } catch (err: any) {
                const validationErrors: { [key: string]: string | undefined } = {};
                err.inner?.forEach((error: any) => {
                    if (error.path) { validationErrors[error.path] = error.message; }
                });
                setErrorModal(validationErrors);
                return;
            }
            await deleteProfessional(currentId, motiveDelete ?? "");
            navigate('/professionals', {
                state: { message: t("professional:modal.messageDeleteProfessionalSuccessfully"), shouldRefresh: true }
            });
            refetch();
        }

        if (modalData.type === "active") {
            await activeProfessional(currentId)
            refetch();
        }
        closeModal();
    }
    return (
        <div>
            <ModalGlobal<UseGetProfessionalsObjectResult>
                show={showModal}
                onClose={closeModal}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                error={errorModal?.endDate || errorModal?.errorModal}
                onSave={confirmSubmitModal}
                handleMotiveDeleteChange={(motive: string | null) => setMotiveDelete(motive)}
                isLoading={isLoading}
            />
            <Row className="mb-2 align-items-center justify-content-between">
                <Col xs={12} md>
                    <h2>{t("professional:titleProfessional")}</h2>
                </Col>
                <Col xs={12} md="auto" className="d-flex align-items-center justify-content-end mt-2 mt-md-0">
                    <Button
                        variant="primary"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={() => navigate(`/professionals/new-professional`)}>
                        {t("professional:buttonNewProfessional")}
                    </Button>
                </Col>
            </Row>
            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert} />
                    )}
                </Col>
                <Col className="text-center" xs={12} md={12}>
                    {error && (
                        <div className="alert alert-danger">{t("common:requestError.errorBadRequest")}</div>
                    )}
                </Col>
            </Row>
            <Row className="mb-2">
                <Col xs={12}>
                    <div className="d-flex align-items-center gap-2">
                        <div className="flex-grow-1">
                            <Search
                                searchText={searchText || ''}
                                onChange={(text) => dispatch(setSearchText({ table: "professionals", text: text }))}
                                onSearch={handleSearchInput}
                                onClear={handleClear} />
                        </div>
                        <Button variant="" onClick={handleClick} aria-controls="collapse-filter-id" style={{ border: 0 }}>
                            <img
                                src="/filter.png"
                                alt="Filtar"
                                style={{ width: '40px', }}
                            />
                        </Button> 
                    </div>
                </Col>
                <Col>
                   
                </Col>
            </Row>
            <Row>
                <div>
                    {!isHideFilter && filters && (
                        <div className="dashboard-section">
                            <FiltersContainer
                                filters={filters}
                                isFilterVisible={isFilterVisible && !isHideFilter}
                                tableKey="professionals"
                                historicalKey="professionals"
                            />
                        </div>
                    )}
                </div>
            </Row>

            <Row>
                <Form>
                    <Form.Check
                        className="my-4"
                        type="switch"
                        id="custom-switch"
                        label={t("common:buttons.toggleActiveRecords")}
                        onChange={handleFilterByActives}
                        checked={onlyActiveToggleChange}
                    />
                </Form>
            </Row>

            <AppTable<ProfessionalDto>
                data={professionalsPagination?.items || []}
                columns={columns}
                emptyMessage={"No records found"}
                t={t}
                hasActions={true}
                hasPagination={true}
                currentPage={currentPage}
                totalPages={professionalsPagination?.totalPages || 0}
                pageSize={itemsPerPage}
                paginationType="professionals"
                onExportCsv={handleExportCsv}
                canActive={(item) => item.deletedDate != null}
                onActive={(item) => { openModal(item, t("professional:modal.modalActiveProfessionalTitle"), t("professional:modal.modalActiveProfessionalSubtitle", { name: item.nameAndSurname }), "active", "") }}
                onEdit={(id: number) => navigate(`/professionals/${id}`)}
                onDelete={(item) => { openModal(item, t("professional:modal.modalDeleteProfessionalTitle"), t("professional:modal.modalDeleteProfessionalSubtitle"), "delete", "") }}
                sectionPermission={SectionPermissionTypeEnum.PROFESSIONAL_MANAGEMENT}
            />
        </div>
    )
}